import React, { useEffect, useState, useCallback } from "react";
import { 
  Flame, 
  TrendingUp, 
  Clock, 
  Users, 
  MapPin, 
  ChevronRight, 
  Sparkles, 
  Heart,
  Globe,
  Video,
  Star,
  Zap,
  Eye
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/contexts/AuthContext";
import { useUserInterests } from "@/hooks/useInterests";
import IntentIcon from "./IntentIcon";
import StageCard from "./StageCard";
import ExhibitionEmptyState from "./ExhibitionEmptyState";
import { cn } from "@/lib/utils";

interface HotBooth {
  id: string;
  name: string;
  topic: string;
  exhibition_intent: string | null;
  participants_count: number;
  max_participants: number;
  is_video_enabled: boolean;
  host_name: string | null;
  host_photo: string | null;
  city_name: string | null;
  country_name: string | null;
  country_flag: string | null;
}

interface TrendingCity {
  id: string;
  name: string;
  country_code: string;
  country_name: string;
  country_flag: string;
  booth_count: number;
  participant_count: number;
}

interface RecentActivity {
  type: "new_booth" | "trending" | "popular";
  booth_id: string;
  booth_name: string;
  city_name: string;
  country_flag: string;
  message: string;
  timestamp: string;
}

interface ExhibitionHomeTabProps {
  totalBooths: number;
  totalParticipants: number;
  countriesCount: number;
  remainingRequests: number;
  onEnterExhibition: () => void;
  onPreviewBooth: (boothId: string) => void;
  onNavigateToCity: (cityId: string, countryCode: string) => void;
}

const ExhibitionHomeTab = React.forwardRef<HTMLDivElement, ExhibitionHomeTabProps>((
  {
    totalBooths,
    totalParticipants,
    countriesCount,
    remainingRequests,
    onEnterExhibition,
    onPreviewBooth,
    onNavigateToCity,
  },
  ref
) => {
  const { user } = useAuthContext();
  const { userInterestIds } = useUserInterests();
  
  const [hotBooths, setHotBooths] = useState<HotBooth[]>([]);
  const [trendingCities, setTrendingCities] = useState<TrendingCity[]>([]);
  const [recommendedBooths, setRecommendedBooths] = useState<HotBooth[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch hot booths (most active right now)
  const fetchHotBooths = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("live_arenas")
        .select(`
          id,
          name,
          topic,
          exhibition_intent,
          is_video_enabled,
          max_participants,
          profiles:created_by (name, photo_url),
          cities:city_id (name, countries:country_code (name, flag))
        `)
        .eq("is_exhibition_booth", true)
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;

      // Get participant counts
      const boothsWithCounts = await Promise.all(
        (data || []).map(async (booth) => {
          const { count } = await supabase
            .from("arena_participants")
            .select("*", { count: "exact", head: true })
            .eq("arena_id", booth.id)
            .is("left_at", null);

          const host = booth.profiles as any;
          const city = booth.cities as any;
          const country = city?.countries as any;

          return {
            id: booth.id,
            name: booth.name,
            topic: booth.topic,
            exhibition_intent: booth.exhibition_intent,
            is_video_enabled: booth.is_video_enabled ?? false,
            participants_count: count || 0,
            max_participants: booth.max_participants || 8,
            host_name: host?.name,
            host_photo: host?.photo_url,
            city_name: city?.name,
            country_name: country?.name,
            country_flag: country?.flag,
          };
        })
      );

      // Sort by participant count (most active first)
      boothsWithCounts.sort((a, b) => b.participants_count - a.participants_count);
      setHotBooths(boothsWithCounts.slice(0, 6));
    } catch (error) {
      console.error("Error fetching hot booths:", error);
    }
  }, []);

  // Fetch trending cities
  const fetchTrendingCities = useCallback(async () => {
    try {
      // Get cities with active booths
      const { data, error } = await supabase
        .from("cities")
        .select(`
          id,
          name,
          country_code,
          countries:country_code (name, flag)
        `)
        .eq("is_active", true);

      if (error) throw error;

      // Get booth and participant counts for each city
      const citiesWithCounts = await Promise.all(
        (data || []).map(async (city) => {
          const { count: boothCount } = await supabase
            .from("live_arenas")
            .select("*", { count: "exact", head: true })
            .eq("city_id", city.id)
            .eq("is_exhibition_booth", true)
            .eq("is_active", true);

          // Get participant count through arena_participants
          const { data: arenaIds } = await supabase
            .from("live_arenas")
            .select("id")
            .eq("city_id", city.id)
            .eq("is_exhibition_booth", true)
            .eq("is_active", true);

          let participantCount = 0;
          if (arenaIds && arenaIds.length > 0) {
            const { count } = await supabase
              .from("arena_participants")
              .select("*", { count: "exact", head: true })
              .in("arena_id", arenaIds.map(a => a.id))
              .is("left_at", null);
            participantCount = count || 0;
          }

          const country = city.countries as any;

          return {
            id: city.id,
            name: city.name,
            country_code: city.country_code,
            country_name: country?.name || "",
            country_flag: country?.flag || "",
            booth_count: boothCount || 0,
            participant_count: participantCount,
          };
        })
      );

      // Filter to cities with activity and sort
      const activeCities = citiesWithCounts
        .filter(c => c.booth_count > 0 || c.participant_count > 0)
        .sort((a, b) => (b.participant_count * 2 + b.booth_count) - (a.participant_count * 2 + a.booth_count));

      setTrendingCities(activeCities.slice(0, 5));
    } catch (error) {
      console.error("Error fetching trending cities:", error);
    }
  }, []);

  // Fetch recommended booths based on user interests
  const fetchRecommendedBooths = useCallback(async () => {
    if (!user || userInterestIds.size === 0) return;

    try {
      // Get user's interest IDs
      const interestIds = Array.from(userInterestIds);

      // Find booths tagged with user's interests
      const { data: taggedArenas, error: tagError } = await supabase
        .from("arena_tags")
        .select("arena_id")
        .in("interest_id", interestIds);

      if (tagError) throw tagError;

      if (!taggedArenas || taggedArenas.length === 0) return;

      const arenaIds = [...new Set(taggedArenas.map(t => t.arena_id))];

      const { data, error } = await supabase
        .from("live_arenas")
        .select(`
          id,
          name,
          topic,
          exhibition_intent,
          is_video_enabled,
          max_participants,
          profiles:created_by (name, photo_url),
          cities:city_id (name, countries:country_code (name, flag))
        `)
        .in("id", arenaIds)
        .eq("is_exhibition_booth", true)
        .eq("is_active", true)
        .limit(4);

      if (error) throw error;

      const boothsWithCounts = await Promise.all(
        (data || []).map(async (booth) => {
          const { count } = await supabase
            .from("arena_participants")
            .select("*", { count: "exact", head: true })
            .eq("arena_id", booth.id)
            .is("left_at", null);

          const host = booth.profiles as any;
          const city = booth.cities as any;
          const country = city?.countries as any;

          return {
            id: booth.id,
            name: booth.name,
            topic: booth.topic,
            exhibition_intent: booth.exhibition_intent,
            is_video_enabled: booth.is_video_enabled ?? false,
            participants_count: count || 0,
            max_participants: booth.max_participants || 8,
            host_name: host?.name,
            host_photo: host?.photo_url,
            city_name: city?.name,
            country_name: country?.name,
            country_flag: country?.flag,
          };
        })
      );

      setRecommendedBooths(boothsWithCounts);
    } catch (error) {
      console.error("Error fetching recommended booths:", error);
    }
  }, [user, userInterestIds]);

  // Generate recent activity feed
  const generateRecentActivity = useCallback(() => {
    const activities: RecentActivity[] = [];
    
    // Use hot booths to generate activity items
    hotBooths.slice(0, 3).forEach((booth, index) => {
      if (booth.participants_count >= 4) {
        activities.push({
          type: "trending",
          booth_id: booth.id,
          booth_name: booth.name,
          city_name: booth.city_name || "Unknown",
          country_flag: booth.country_flag || "🌍",
          message: `${booth.participants_count} people chatting`,
          timestamp: new Date(Date.now() - index * 5 * 60000).toISOString(),
        });
      } else if (booth.participants_count > 0) {
        activities.push({
          type: "popular",
          booth_id: booth.id,
          booth_name: booth.name,
          city_name: booth.city_name || "Unknown",
          country_flag: booth.country_flag || "🌍",
          message: "Live now",
          timestamp: new Date(Date.now() - index * 8 * 60000).toISOString(),
        });
      }
    });

    setRecentActivity(activities.slice(0, 5));
  }, [hotBooths]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchHotBooths(),
        fetchTrendingCities(),
        fetchRecommendedBooths(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchHotBooths, fetchTrendingCities, fetchRecommendedBooths]);

  // Generate activity after hot booths load
  useEffect(() => {
    if (hotBooths.length > 0) {
      generateRecentActivity();
    }
  }, [hotBooths, generateRecentActivity]);

  // Real-time subscription for booth changes
  useEffect(() => {
    const channel = supabase
      .channel('exhibition-home-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_arenas',
          filter: 'is_exhibition_booth=eq.true',
        },
        () => {
          fetchHotBooths();
          fetchTrendingCities();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchHotBooths, fetchTrendingCities]);

  const formatTimeAgo = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  if (loading) {
    return (
      <div ref={ref} className="mx-4 mt-4 space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-white/30 rounded-2xl" />
          <div className="h-48 bg-white/30 rounded-2xl" />
          <div className="h-32 bg-white/30 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="mx-4 mt-4 space-y-4 pb-4">
      {/* Quick Stats Bar - Icon-first, emotional */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        <div className="flex-shrink-0 px-3 py-2 rounded-full bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-sm font-bold text-green-600">{totalParticipants || "–"}</span>
            <span className="text-xs text-green-600/70">live</span>
          </div>
        </div>
        <div className="flex-shrink-0 px-3 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/20">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500 fill-pink-500/30" />
            <span className="text-sm font-medium text-foreground">{totalBooths} stages</span>
          </div>
        </div>
        <div className="flex-shrink-0 px-3 py-2 rounded-full bg-gradient-to-r from-primary/10 to-blue-500/10 border border-primary/20">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-foreground">{countriesCount} countries</span>
          </div>
        </div>
      </div>

      {/* Live Stages Section - renamed from "Hot Booths" */}
      {hotBooths.length > 0 ? (
        <div className="rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-red-500">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-foreground">Live Stages</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span>{hotBooths.length} active</span>
            </div>
          </div>

          <div className="space-y-2">
            {hotBooths.slice(0, 4).map((booth) => (
              <StageCard
                key={booth.id}
                id={booth.id}
                name={booth.name}
                topic={booth.topic}
                cityName={booth.city_name || undefined}
                countryFlag={booth.country_flag || undefined}
                participantsCount={booth.participants_count}
                maxParticipants={booth.max_participants}
                isVideoEnabled={booth.is_video_enabled}
                hostName={booth.host_name || undefined}
                hostPhoto={booth.host_photo || undefined}
                intent={booth.exhibition_intent || undefined}
                onWatch={() => onPreviewBooth(booth.id)}
                variant="compact"
              />
            ))}
          </div>

          {hotBooths.length > 4 && (
            <Button
              variant="ghost"
              onClick={onEnterExhibition}
              className="w-full mt-3 text-sm text-pink-600 hover:text-pink-700 hover:bg-pink-50"
            >
              <Eye className="w-4 h-4 mr-1.5" />
              View all {hotBooths.length} live stages
            </Button>
          )}
        </div>
      ) : (
        <ExhibitionEmptyState
          variant="starting-soon"
          watchersCount={totalParticipants}
          onHostStage={onEnterExhibition}
          onExplore={onEnterExhibition}
        />
      )}

      {/* Trending Cities */}
      {trendingCities.length > 0 && (
        <div className="rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-foreground">Trending Cities</span>
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {trendingCities.map((city) => (
              <button
                key={city.id}
                onClick={() => onNavigateToCity(city.id, city.country_code)}
                className="flex-shrink-0 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-white/70 hover:bg-white transition-all border border-white/60 hover-lift press-scale"
              >
                <span className="text-xl">{city.country_flag}</span>
                <div className="text-left">
                  <p className="font-medium text-foreground text-sm whitespace-nowrap">{city.name}</p>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Heart className="w-3 h-3 text-pink-400 fill-pink-400" />
                    <span>{city.booth_count} stages</span>
                    <span>·</span>
                    <span className="text-green-600">{city.participant_count} live</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* For You - Recommended Stages */}
      {recommendedBooths.length > 0 && (
        <div className="rounded-2xl backdrop-blur-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-foreground">For You</span>
            <span className="text-xs text-muted-foreground/70">Based on your interests</span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {recommendedBooths.slice(0, 4).map((booth) => (
              <button
                key={booth.id}
                onClick={() => onPreviewBooth(booth.id)}
                className="p-3 rounded-xl bg-white/70 hover:bg-white transition-all text-left hover-lift press-scale"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-base">{booth.country_flag}</span>
                  <div className="flex items-center gap-1">
                    {booth.exhibition_intent && (
                      <IntentIcon intent={booth.exhibition_intent} size="sm" />
                    )}
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                  </div>
                </div>
                <p className="font-semibold text-foreground text-sm truncate">{booth.name}</p>
                <p className="text-xs text-muted-foreground truncate">{booth.city_name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Live Activity Feed */}
      {recentActivity.length > 0 && (
        <div className="rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-foreground">What's Happening</span>
          </div>

          <div className="space-y-2">
            {recentActivity.map((activity, index) => (
              <button
                key={`${activity.booth_id}-${index}`}
                onClick={() => onPreviewBooth(activity.booth_id)}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/60 transition-all text-left"
              >
                <span className="text-lg">{activity.country_flag}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    <span className="font-medium">{activity.booth_name}</span>
                    {" · "}
                    <span className="text-muted-foreground">{activity.message}</span>
                  </p>
                </div>
                <span className="text-xs text-muted-foreground/70 flex-shrink-0">
                  {formatTimeAgo(activity.timestamp)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Subtle join requests indicator */}
      <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/60 pt-2">
        <Users className="w-3.5 h-3.5" />
        <span>{remainingRequests} join requests left today</span>
      </div>
    </div>
  );
});

ExhibitionHomeTab.displayName = "ExhibitionHomeTab";

export default ExhibitionHomeTab;
